import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquareText, Sparkles, Loader2, UserPlus, Copy, MessageCircle, FileText, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';
import { waLink } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function WhatsAppInbox() {
  const navigate = useNavigate();
  const [pesan, setPesan] = useState('');
  const [loading, setLoading] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [draft, setDraft] = useState('');
  const [savedLead, setSavedLead] = useState(null);
  const [saving, setSaving] = useState(false);

  const proses = async () => {
    if (!pesan.trim()) { toast.error('Tempel pesan WhatsApp dulu'); return; }
    setLoading(true); setExtracted(null); setSavedLead(null);
    try {
      const r = await api.post('/ai/whatsapp', { pesan });
      setExtracted(r.data);
      setDraft(r.data.draft_balasan || '');
      toast.success('Pesan diproses AI');
    } catch (e) { !e?.__gated && toast.error(e?.response?.data?.detail || 'AI gagal memproses'); } finally { setLoading(false); }
  };

  const saveLead = async () => {
    setSaving(true);
    try {
      const r = await api.post('/ai/whatsapp/ingest', {
        nama_pelanggan: extracted.nama_pelanggan, nomor_wa: extracted.nomor_wa,
        alamat: extracted.alamat, kebutuhan: extracted.kebutuhan,
      });
      setSavedLead(r.data);
      toast.success('Lead tersimpan di Pelanggan');
    } catch (e) { toast.error('Gagal menyimpan lead'); } finally { setSaving(false); }
  };

  const copyDraft = () => { navigator.clipboard?.writeText(draft); toast.success('Balasan disalin'); };
  const sendWA = () => { if (extracted?.nomor_wa) window.open(waLink(extracted.nomor_wa, draft), '_blank'); else { copyDraft(); toast.info('Nomor WA tidak terdeteksi, balasan disalin.'); } };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 font-display text-xl font-bold"><MessageSquareText className="h-5 w-5 text-primary" /> Inbox WhatsApp (AI)</h1>
        <p className="text-sm text-muted-foreground">Tempel chat pelanggan — AI ekstrak kebutuhan, buat lead, & draft balasan.</p>
      </div>

      <div className="rounded-xl border bg-card p-4">
        <Label className="mb-1.5 block">Pesan WhatsApp Masuk</Label>
        <Textarea value={pesan} onChange={(e) => setPesan(e.target.value)} rows={4} data-testid="wa-inbox-pesan"
          placeholder="cth: Halo pak, saya Andi mau tanya harga jendela sliding 2x1.5m untuk 4 unit. Lokasi Bekasi. WA 0812xxxx" />
        <Button onClick={proses} disabled={loading} className="mt-2 rounded-xl" data-testid="wa-inbox-proses">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Proses dengan AI
        </Button>
      </div>

      {extracted && (
        <>
          <div className="rounded-xl border bg-card p-4">
            <h2 className="mb-2 font-display text-sm font-semibold">Hasil Ekstraksi</h2>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-muted-foreground">Nama:</span> <b>{extracted.nama_pelanggan || '-'}</b></div>
              <div><span className="text-muted-foreground">No. WA:</span> <b>{extracted.nomor_wa || '-'}</b></div>
              <div className="col-span-2"><span className="text-muted-foreground">Produk:</span> <b>{extracted.produk || '-'}</b></div>
              <div className="col-span-2"><span className="text-muted-foreground">Kebutuhan:</span> {extracted.kebutuhan || '-'}</div>
              {extracted.alamat && <div className="col-span-2"><span className="text-muted-foreground">Alamat:</span> {extracted.alamat}</div>}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {savedLead ? (
                <span className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Lead tersimpan</span>
              ) : (
                <Button onClick={saveLead} disabled={saving} className="rounded-xl" data-testid="wa-inbox-simpan-lead">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />} Simpan sebagai Lead</Button>
              )}
              {savedLead && <Button variant="outline" onClick={() => navigate(`/pelanggan/${savedLead.id}`)} className="rounded-xl">Buka Pelanggan</Button>}
              <Button variant="outline" onClick={() => navigate('/penawaran/baru')} className="rounded-xl"><FileText className="mr-2 h-4 w-4" /> Buat Penawaran</Button>
            </div>
          </div>

          <div className="rounded-xl border bg-card p-4">
            <h2 className="mb-2 font-display text-sm font-semibold">Draft Balasan</h2>
            <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={6} className="text-sm" data-testid="wa-inbox-draft" />
            <div className="mt-2 flex gap-2">
              <Button variant="outline" onClick={copyDraft} className="rounded-xl"><Copy className="mr-2 h-4 w-4" /> Salin</Button>
              <Button onClick={sendWA} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90"><MessageCircle className="mr-2 h-4 w-4" /> Kirim WhatsApp</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
